/* eslint-disable no-console */
import React, { useEffect } from 'react';
import {
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';

import { Box, WrappedTextInput, Button } from 'components';
import { StepProps } from 'common/types';
import { errorMessageHandler, formatAmount } from 'common/utils/helpers';
import { getInvestorDetails } from 'redux-modules/investor/Actions';
import { addRolloverInvestment, getSubscriberSSN } from 'services';
import { RootState } from 'redux-modules/store/rootState';
import { REINVEST_TYPE } from 'common/utils/constants';

import styles from './styles';
import {
  ReInvestPartialSubscriptionSchema,
  ReInvestFullSubscriptionSchema,
} from '../validations';

interface ReInvestSubscriptionProps extends StepProps {
  reInvestPartial: boolean;
  distributionData: any;
}

const ReInvestSubscription = ({
  handleNext,
  stepName,
  spv,
  investor,
  kycId,
  kycDetail,
  reInvestPartial,
  distributionData,
}: ReInvestSubscriptionProps) => {
  const dispatch = useDispatch();
  const classes = styles();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [showSSN, setShowSSN] = React.useState<boolean>(false);
  const [isEditModeSSN, setIsEditModeSSN] = React.useState<boolean>(true);

  // const { dealList } = useSelector(({ Deals }: RootState) => Deals);
  const { investmentProcess } = useSelector(
    ({ Investor }: RootState) => Investor,
  );

  const { investmentDetail } = investor;
  const investmentData = investmentDetail?.find(
    (investment: any) => investment.spv === spv && investment.kycId === kycId,
  );

  const { handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(
      reInvestPartial
        ? ReInvestPartialSubscriptionSchema({
            amount: get(distributionData, 'balance', 0).toFixed(2),
          })
        : ReInvestFullSubscriptionSchema({
            amount: get(distributionData, 'balance', 0).toFixed(2),
          }),
    ),
  });

  const handleClickShowSSN = () => {
    if (!showSSN) {
      getSubscriberSSN(get(investmentData, 'id'))
        .then((res: any) => {
          setValue('subscriberEinOrSSN', res);
          setShowSSN(!showSSN);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
        });
    } else {
      setValue('subscriberEinOrSSN', get(investmentData, 'subscriberEinOrSSN'));
      setShowSSN(!showSSN);
    }
  };

  const handleClickEditSSN = () => {
    if (!isEmpty(get(investmentData, 'id'))) {
      getSubscriberSSN(get(investmentData, 'id'))
        .then((res: any) => {
          setValue('subscriberEinOrSSN', res);
          setIsEditModeSSN(!isEditModeSSN);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
        });
    }
  };

  const handleCloseEditSSN = () => {
    setIsEditModeSSN(!isEditModeSSN);
    setValue('ssn', get(kycDetail, 'ssn'));
  };

  const handleMouseDownSSN = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const getRemainingWireAmount = (
    rolloverAvailableBalance: any,
    investmentAmount: any,
  ) => {
    let remainingWireAmount = 0;
    if (rolloverAvailableBalance >= investmentAmount) {
      remainingWireAmount = 0;
    } else if (investmentAmount > rolloverAvailableBalance) {
      remainingWireAmount =
        parseFloat(investmentAmount) - parseFloat(rolloverAvailableBalance);
    }
    return remainingWireAmount;
  };
  const onSubmit = (data: any): void => {
    setIsLoading(true);
    const payload = {
      ...data,
      investmentCurrentStep: stepName,
      esignStatus: get(investmentProcess, 'docuSignDocumentSigned')
        ? 'COMPLETED'
        : get(investmentData, 'esignStatus', 'PENDING'),
      rolloverSpvId: get(distributionData, 'spv.id'),
      rolloverInvestType: reInvestPartial
        ? REINVEST_TYPE.PARTIAL
        : REINVEST_TYPE.FULL,
      investorId: get(investor, 'id'),
      spv: get(investmentData, 'spv') || spv,
    };
    if (get(investmentData, 'id')) payload.id = get(investmentData, 'id');
    if (!isEditModeSSN) delete payload.subscriberEinOrSSN;
    addRolloverInvestment(payload)
      .then((res: any) => {
        console.log('res----', res);
        setIsLoading(false);
        dispatch(getInvestorDetails(false));
        setIsEditModeSSN(!isEditModeSSN);
        handleNext();
      })
      .catch((err: any) => {
        console.log('err---', err);
        setIsLoading(false);
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
      });
  };

  useEffect(() => {
    if (get(investmentData, 'id')) {
      setValue('investmentAmount', get(investmentData, 'investmentAmount'));
      setValue('subscriberName', get(investmentData, 'subscriberName'));
      setValue('subscriberEinOrSSN', get(investmentData, 'subscriberEinOrSSN'));
      if (!isEmpty(get(investmentData, 'subscriberEinOrSSN')))
        setIsEditModeSSN(false);
    } else {
      const partialAmount: any = get(distributionData, 'balance') / 2;
      setValue(
        'investmentAmount',
        !reInvestPartial
          ? get(distributionData, 'balance').toFixed(2)
          : partialAmount.toFixed(2),
      );
      // setValue('subscriberName', get(kycDetail, 'signatoryLegalName'));
      // setValue('subscriberEinOrSSN', get(kycDetail, 'ssn'));
    }
  }, [investmentData]);

  return (
    <Box className={classes.subscriptionForm}>
      <Typography variant="h3" className={classes.investNowFormHeader}>
        Roll-over {reInvestPartial ? 'partial' : 'full'} amount:
      </Typography>
      <Typography className={classes.distributionAmountTxt} variant="body1">
        Your upcoming roll-over amount is{' '}
        {formatAmount(get(distributionData, 'balance'), {
          maximumFractionDigits: 2,
        })}
      </Typography>
      {errorMessage && (
        <Typography variant="body1" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      )}
      <form
        onSubmit={
          get(investmentProcess, 'docuSignDocumentSigned')
            ? () => handleNext()
            : handleSubmit(onSubmit)
        }
        data-testid="subscriptionForm"
        className={classes.validateForm}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.labelText} variant="body1">
              Amount getting rolled-over to future Bridge to Demo Day SPVs
              {/* Amount you are investing in SPV{' '}
              {get(find(dealList, { id: spv }), 'deal', '')}.: */}
            </Typography>
            <Controller
              name="investmentAmount"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="number"
                    label=""
                    placeholder="Enter Investment Amount"
                    className={classes.textValInput}
                    InputProps={{
                      classes: { root: classes.inputRoot },
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={onChange}
                    error={error?.message ? true : false}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                  {!error?.message &&
                    value !== undefined &&
                    value !== null &&
                    getRemainingWireAmount(
                      get(distributionData, 'balance'),
                      Number(value),
                    ) > 0 && (
                      <span className={classes.infoText}>
                        You are investing more than the distribution amount,
                        Please wire the extra $
                        {getRemainingWireAmount(
                          get(distributionData, 'balance'),
                          Number(value),
                        ).toFixed(2)}{' '}
                        in the next steps.
                      </span>
                    )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.labelText} variant="body1">
              Subscriber Name
            </Typography>
            <Controller
              name="subscriberName"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="Name"
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.labelText} variant="body1">
              EIN/SSN of Subscriber
            </Typography>
            <Controller
              name="subscriberEinOrSSN"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="EIN/SSN of Subscriber"
                    className={classes.textValInput}
                    InputProps={{
                      classes: { root: classes.inputRoot },
                      endAdornment: (
                        <>
                          {!isEditModeSSN &&
                            get(investmentData, 'subscriberEinOrSSN') &&
                            !get(
                              investmentProcess,
                              'docuSignDocumentSigned',
                            ) && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="edit"
                                  onClick={handleClickEditSSN}
                                  onMouseDown={handleMouseDownSSN}
                                  edge="end"
                                >
                                  <EditIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                          {isEditModeSSN &&
                            get(investmentData, 'subscriberEinOrSSN') &&
                            !get(
                              investmentProcess,
                              'docuSignDocumentSigned',
                            ) && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="close"
                                  onClick={handleCloseEditSSN}
                                  onMouseDown={handleMouseDownSSN}
                                  edge="end"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                          {get(investmentData, 'subscriberEinOrSSN') && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle visibility"
                                onClick={handleClickShowSSN}
                                onMouseDown={handleMouseDownSSN}
                                edge="end"
                                disabled={isEditModeSSN}
                              >
                                {showSSN ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={
                      !isEditModeSSN ||
                      get(investmentProcess, 'docuSignDocumentSigned')
                    }
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Box className={classes.nextPrevButtonBox}>
          {/* <Button
            variant="outlined"
            className={classes.prevButton}
            name="Previous"
            disabled={isLoading}
            onClick={() => handleBack()}
          /> */}
          <Button
            type="submit"
            className={classes.nextButton}
            name="Next"
            isLoading={isLoading}
            disabled={isLoading}
          />
        </Box>
      </form>
    </Box>
  );
};
export default ReInvestSubscription;
